/**
 * Form definition utilities — i18n transformation, dynamic field injection.
 */

import * as T from "../utilities/frontendTypes";
import { FieldDefinition, FieldValidator, FormDefinition, selectionData } from "../Components/Form/Form";
import { FormGroupDefinition } from "../Components/FormGroup";
import { profileFormCompanyInfoDefinition } from "./profileFormDefitions";
import { basic_text_validator, url_validator, multiselect_validator } from "./formFieldValidators";

// Swap a validator rule's message for its frMessage, spread-copying so the
// shared validator singletons in formFieldValidators.ts are never mutated.
// RegExp/boolean/number shorthand rules ("message" not in them) pass through.
const translateRule = <R,>(rule: R): R =>
  rule && typeof rule === "object" && "message" in (rule as object)
    ? ({ ...(rule as object), message: (rule as any).frMessage || (rule as any).message } as R)
    : rule;

const translateValidator = (validator?: FieldValidator): FieldValidator | undefined =>
  validator && {
    ...validator,
    required: translateRule(validator.required),
    minLength: translateRule(validator.minLength),
    maxLength: translateRule(validator.maxLength),
    min: translateRule(validator.min),
    max: translateRule(validator.max),
    pattern: translateRule(validator.pattern),
  };

export const returnLangCheckedFormDef = (formDefinition: FormDefinition, lang: string): FormDefinition => {
  if (lang === "fr") {
    const result: FormDefinition = {};
    for (const key in formDefinition) {
      if (formDefinition.hasOwnProperty(key)) {
        const { label, frLabel, frTooltip, tooltip, additionalData, ...otherProperties } = formDefinition[key];

        result[key] = {
          label: frLabel as string,
          frLabel: frLabel,
          ...otherProperties,
        };

        // Check if there is a corresponding frTooltip and update content if needed
        if (tooltip && frTooltip) {
          result[key].tooltip = {
            content: frTooltip.content,
            position: frTooltip.position
          }
        }
        if (otherProperties.frPlaceholder) {
          result[key].placeholder = otherProperties.frPlaceholder;
        }
        if (otherProperties.validator) {
          result[key].validator = translateValidator(otherProperties.validator);
        }
        if (additionalData) {
          const frenchData = additionalData.selectionData.map((item) => ({
            value: item.value,
            text: item.frText || item.text, // Set frText if available, otherwise use text
          }));
          result[key].additionalData = {
            ...additionalData, // keep exclusiveValues etc. — dropping them broke FR-only behavior
            selectionData: frenchData
          }
        }
      }
    }
    return result;
  }
  return formDefinition
}

export const returnLangCheckedFormGroupDef = (formGroupDefinition: FormGroupDefinition, lang: string): FormGroupDefinition => {
  if (lang === "fr") {
    const result: FormGroupDefinition = {}
    for (const key in formGroupDefinition) {
      if (formGroupDefinition.hasOwnProperty(key)) {
        const { sectionLabel, frSectionLabel, formDefinition, ...otherProperties } = formGroupDefinition[key];
        const langCheckedFormDef = returnLangCheckedFormDef(formDefinition, lang);
        result[key] = {
          sectionLabel: frSectionLabel as string,
          frSectionLabel: frSectionLabel,
          formDefinition: langCheckedFormDef,
          ...otherProperties,
        };
      }
    }
    return result
    }
    return formGroupDefinition
  }

export const getModifiedCompanyInfoFormDef = (founderProfile: T.StartupFounderProfile) => {
  let modifiedForm = { ...profileFormCompanyInfoDefinition };
  if (founderProfile && founderProfile.companyInfo && founderProfile.companyInfo.basicInfo && founderProfile.companyInfo.basicInfo.industry === "Other") {
    const newKey = "industryOther";
    const newValue: FieldDefinition = {
      label: `Industry (Other, please specify)`,
      frLabel: "Secteur d'activité (Autre, veuillez préciser)",
      inputType: "text",
      validator: basic_text_validator,
    };

    const newForm: FormDefinition = {};
    for (const [key, value] of Object.entries(modifiedForm)) {
      newForm[key] = value;
      if (key === "industry") {
        newForm[newKey] = newValue;
      }
    }
    modifiedForm = newForm;
  }
  if (founderProfile && founderProfile.companyInfo && founderProfile.companyInfo.basicInfo && founderProfile.companyInfo.basicInfo.tech?.includes("Other")) {
    const newKey = "techOther";
    const newValue: FieldDefinition = {
        label: `Technology, (Other, please specify)`,
        frLabel: "Technologie (Autre, veuillez préciser)",
        inputType: "text",
        placeholder: "https://",
        validator: url_validator,
    };

    const newForm: FormDefinition = {};
    for (const [key, value] of Object.entries(modifiedForm)) {
      newForm[key] = value;
      if (key === "tech") {
        newForm[newKey] = newValue;
      }
    }
    modifiedForm = newForm;
  }
  if (founderProfile && founderProfile.companyInfo && founderProfile.companyInfo.basicInfo && founderProfile.companyInfo.basicInfo.linkedInURL) {
    const newKey = "linkedInURL";
    const newValue: FieldDefinition = {
        label: `Company LinkedIn URL. Please enter "N/A" if your company does not have a LinkedIn profile.`,
        frLabel: `URL LinkedIn de l'entreprise. Veuillez entrer « S.O. » si votre entreprise n'a pas de profil LinkedIn.`,
        inputType: "text",
        placeholder: "https://",
        validator: url_validator,
    };

    const newForm: FormDefinition = {};
    for (const [key, value] of Object.entries(modifiedForm)) {
      newForm[key] = value;
      if (key === "websiteURL") {
        newForm[newKey] = newValue;
      }
    }
    modifiedForm = newForm;
  }
  if (founderProfile && founderProfile.companyInfo && founderProfile.companyInfo.registration && founderProfile.companyInfo.registration.registeredProvincially) {
    const newKey = "province";
    const newValue: FieldDefinition = {
      conditionalRenderDefinition: {
        fieldKey: "registeredProvincially",
        renderWhenTrue: false,
      },
      label: "Registered Provinces and Territories",
      inputType: "multiSelectNarrow",
      frLabel: "Provinces et territoires enregistrés",
      validator: multiselect_validator,
      additionalData: {
        selectionData: [
          {
            value: "AB",
            text: "Alberta",
            frText: "Alberta"
          },
          {
            value: "BC",
            text: "British Columbia",
            frText: "Colombie-Britannique"
          },
          {
            value: "MB",
            text: "Manitoba",
            frText: "Manitoba"
          },
          {
            value: "NB",
            text: "New Brunswick",
            frText: "Nouveau-Brunswick"
          },
          {
            value: "NL",
            text: "Newfoundland and Labrador",
            frText: "Terre-Neuve-et-Labrador"
          },
          {
            value: "NT",
            text: "Northwest Territories",
            frText: "Territoires du Nord-Ouest"
          },
          {
            value: "NS",
            text: "Nova Scotia",
            frText: "Nouvelle-Écosse"
          },
          {
            value: "NU",
            text: "Nunavut",
            frText: "Nunavut"
          },
          {
            value: "ON",
            text: "Ontario",
            frText: "Ontario"
          },
          {
            value: "PE",
            text: "Prince Edward Island",
            frText: "Île-du-Prince-Édouard"
          },
          {
            value: "QC",
            text: "Quebec",
            frText: "Québec"
          },
          {
            value: "SK",
            text: "Saskatchewan",
            frText: "Saskatchewan"
          },
          {
            value: "YT",
            text: "Yukon",
            frText: "Yukon"
          },
        ]
      }
    };

    const newForm: FormDefinition = {};
    for (const [key, value] of Object.entries(modifiedForm)) {
      newForm[key] = value;
      if (key === "registeredProvincially") {
        newForm[newKey] = newValue;
      }
    }
    modifiedForm = newForm;
  }
  return modifiedForm
}
